import React, { createContext, useCallback, useState, ReactNode } from "react";

export interface QAItem {
	question: string;
	answer: string;
}

export interface ConversationMessage {
	role: "user" | "assistant";
	content: string;
}

export interface QAContextType {
	// State
	currentQuestion: string;
	currentAnswer: string;
	qaHistory: QAItem[];
	conversationHistory: ConversationMessage[];
	isLoadingQuestion: boolean;
	aiError: string | null;

	// Actions
	setCurrentQuestion: (question: string) => void;
	setCurrentAnswer: (answer: string) => void;
	setQaHistory: (history: QAItem[]) => void;
	setConversationHistory: (history: ConversationMessage[]) => void;
	setIsLoadingQuestion: (loading: boolean) => void;
	setAiError: (error: string | null) => void;

	// Computed
	questionCount: number;
	hasReachedMaxQuestions: () => boolean;

	// Methods
	addQAToHistory: (question: string, answer: string) => QAItem[];
	addConversationMessage: (role: "user" | "assistant", content: string) => void;
	resetQA: () => void;
}

export const QAContext = createContext<QAContextType | undefined>(undefined);

interface QAProviderProps {
	children: ReactNode;
}

export function QAProvider({ children }: QAProviderProps) {
	const [currentQuestion, setCurrentQuestion] = useState("");
	const [currentAnswer, setCurrentAnswer] = useState("");
	const [qaHistory, setQaHistory] = useState<QAItem[]>([]);
	const [conversationHistory, setConversationHistory] = useState<
		ConversationMessage[]
	>([]);
	const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
	const [aiError, setAiError] = useState<string | null>(null);

	const questionCount = qaHistory.length;

	const hasReachedMaxQuestions = useCallback(() => {
		return questionCount >= 6;
	}, [questionCount]);

	const addQAToHistory = useCallback(
		(question: string, answer: string): QAItem[] => {
			const newQA = { question, answer };
			const updated = [...qaHistory, newQA];
			setQaHistory(updated);
			return updated;
		},
		[qaHistory],
	);

	const addConversationMessage = useCallback(
		(role: "user" | "assistant", content: string) => {
			setConversationHistory((prev) => [...prev, { role, content }]);
		},
		[],
	);

	const resetQA = useCallback(() => {
		setCurrentQuestion("");
		setCurrentAnswer("");
		setQaHistory([]);
		setConversationHistory([]);
		setIsLoadingQuestion(false);
		setAiError(null);
	}, []);

	const value: QAContextType = {
		currentQuestion,
		currentAnswer,
		qaHistory,
		conversationHistory,
		isLoadingQuestion,
		aiError,
		setCurrentQuestion,
		setCurrentAnswer,
		setQaHistory,
		setConversationHistory,
		setIsLoadingQuestion,
		setAiError,
		questionCount,
		hasReachedMaxQuestions,
		addQAToHistory,
		addConversationMessage,
		resetQA,
	};

	return <QAContext.Provider value={value}>{children}</QAContext.Provider>;
}
